import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiSend, FiCheckCircle } from 'react-icons/fi';
import useRequest from '../../../hooks/useRequest';

function DashboardSupport() {
  const { sendRequest, loading } = useRequest();
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    topic: '',
    message: ''
  });

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    // Company name validation
    if (!formData.company_name) {
      newErrors.company_name = 'Company name is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Topic validation
    if (!formData.topic) {
      newErrors.topic = 'Please select a topic';
    }

    // Message validation
    if (!formData.message) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        contact_email: formData.email,
        topic: formData.topic,
        details: formData.message,
        name: formData.name,
        company_name: formData.company_name
      };

      await sendRequest('POST', '/api/support/', submitData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        company_name: '',
        email: '',
        topic: '',
        message: ''
      });
    } catch (error) {
      if (error.data) {
        setErrors({
          email: error.data.contact_email,
          topic: error.data.topic,
          message: error.data.details
        });
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      className="p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-2xl mx-auto">
        <motion.div variants={itemVariants} className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">
            Need Help?
          </h1>
          <p className="text-gray-600">
            Submit your details below and our support team will get back to you shortly.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-8">
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <FiFileText className="w-5 h-5" />
            <span className="text-sm font-medium">Get Document</span>
          </motion.button>
        </motion.div>

        {isSubmitted ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 p-6 rounded-lg text-center"
          >
            <FiCheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Message Sent Successfully!
            </h3>
            <p className="text-green-600">
              Our support team will get back to you shortly.
            </p>
          </motion.div>
        ) : (
          <motion.form 
            variants={containerVariants}
            className="space-y-6 bg-white p-6 rounded-xl shadow-sm"
            onSubmit={handleSubmit}
          >
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name..."
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.name ? 'border-red-500' : 'border-gray-200'
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name}</p>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter your company name..."
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.company_name ? 'border-red-500' : 'border-gray-200'
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors`}
              />
              {errors.company_name && (
                <p className="text-red-500 text-sm">{errors.company_name}</p>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email..."
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Topic
              </label>
              <select
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.topic ? 'border-red-500' : 'border-gray-200'
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors`}
              >
                <option value="" disabled>Select a topic...</option>
                <option value="general">General Inquiry</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
              </select>
              {errors.topic && (
                <p className="text-red-500 text-sm">{errors.topic}</p>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Type your message here..."
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.message ? 'border-red-500' : 'border-gray-200'
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors`}
              />
              {errors.message && (
                <p className="text-red-500 text-sm">{errors.message}</p>
              )}
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-opacity-70 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <FiSend className="w-5 h-5" />
                    <span>Contact Support</span>
                  </>
                )}
              </motion.button>
            </motion.div>
          </motion.form>
        )}
      </div>
    </motion.div>
  );
}

export default DashboardSupport; 